import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';
import { locations } from '@/data/locations';
import { MapPin, ArrowRight, Phone, Truck, Shield, Clock } from 'lucide-react';

export default function LocationsPage() {
  const london = locations.filter((l) => l.region === 'London');
  const southEast = locations.filter((l) => l.region === 'South East England');

  return (
    <>
      <SEO
        title="Projector Repair Locations"
        description="Local projector repair collection across London and South East England. Free insured courier collection, component-level repairs, 90-day warranty and 4-7 day turnaround."
        canonical="https://projectorrepairlondon.co.uk/locations"
      />

      <section className="bg-[#0057B7] pt-32 pb-16 md:pt-40 md:pb-24">
        <div className="container-custom">
          <nav className="mb-6"><ol className="flex items-center gap-2 text-sm">
            <li><Link to="/" className="text-white/70 hover:text-white transition-colors">Home</Link></li>
            <li className="text-white/40">/</li>
            <li className="text-white font-medium">Locations</li>
          </ol></nav>
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/10 rounded-full text-xs font-label text-[#FFDD00] mb-4"><MapPin className="w-3.5 h-3.5" />Local Coverage</span>
          <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-extrabold text-white max-w-3xl leading-tight">Projector Repair Across London & South East England</h1>
          <p className="mt-4 text-white/80 max-w-2xl leading-relaxed">Choose your nearest service area. Every location includes insured collection, laboratory diagnosis, a fixed-price quote and a 90-day warranty on completed repairs.</p>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl">
            {[{ icon: Truck, label: 'Free insured collection' }, { icon: Shield, label: '90-day repair warranty' }, { icon: Clock, label: '4-7 day turnaround' }].map((f) => (
              <div key={f.label} className="flex items-center gap-3 bg-white/10 border border-white/15 rounded-lg px-4 py-3 text-white/90 text-sm"><f.icon className="w-4 h-4 text-[#FFDD00]" />{f.label}</div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-14 lg:py-18">
        <div className="container-custom">
          <div className="flex items-end justify-between gap-6 mb-8">
            <div>
              <span className="font-label text-[#0057B7]">Greater London</span>
              <h2 className="font-heading text-3xl font-bold text-[#0057B7] mt-3">London Service Areas</h2>
            </div>
            <a href="tel:+443330064326" className="hidden md:inline-flex items-center gap-2 text-[#0057B7] font-semibold"><Phone className="w-4 h-4" />0333 006 4326</a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {london.map((l) => (
              <Link key={l.slug} to={`/locations/${l.slug}`} className="group bg-[#E8F0FE] rounded-xl p-6 border border-[#0057B7]/10 hover:border-[#0057B7]/30 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#0057B7]">Projector Repair {l.name}</h3>
                    <p className="mt-1 text-sm text-[#0057B7]/70">{l.county} • {l.postcodes}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#0057B7] group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="mt-4 text-sm text-[#0057B7]/75 line-clamp-3">{l.intro}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#E8F0FE] py-14 lg:py-18">
        <div className="container-custom">
          <div className="mb-8">
            <span className="font-label text-[#0057B7]">South East England</span>
            <h2 className="font-heading text-3xl font-bold text-[#0057B7] mt-3">South East Service Areas</h2>
            <p className="mt-3 text-[#0057B7]/75 max-w-3xl">Local collection pages for Berkshire, Buckinghamshire, Surrey, Hampshire, Kent, Sussex, Oxfordshire and the London commuter belt.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {southEast.map((l) => (
              <Link key={l.slug} to={`/locations/${l.slug}`} className="group bg-white rounded-xl p-6 border border-[#0057B7]/10 hover:border-[#0057B7]/30 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-heading text-lg font-semibold text-[#0057B7]">Projector Repair {l.name}</h3>
                    <p className="mt-1 text-sm text-[#0057B7]/70">{l.county} • {l.postcodes}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-[#0057B7] group-hover:translate-x-1 transition-transform" />
                </div>
                <p className="mt-4 text-sm text-[#0057B7]/75 line-clamp-3">{l.intro}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#FFDD00] py-14">
        <div className="container-custom flex flex-col lg:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-heading text-3xl font-bold text-[#0057B7]">Do not see your town listed?</h2>
            <p className="mt-2 text-[#0057B7]/80">We still collect across the UK, Ireland and EMEA. Send your postcode and projector model for a fast quote.</p>
          </div>
          <Link to="/repair-enquiry" className="inline-flex items-center gap-2 px-8 py-4 bg-[#0057B7] text-[#FFDD00] rounded-lg font-bold hover:bg-[#003D80] transition-all text-lg">Repair Enquiry <ArrowRight className="w-5 h-5" /></Link>
        </div>
      </section>
    </>
  );
}
